import * as React from 'react'

import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import {
  fieldErrorId,
  FieldLabelRow,
  focusFirstInvalidField,
  FormValidation,
  getPhoneNumberError,
} from '~/client/src/shared/components/AuthLayout'
import { Button } from '~/client/src/shared/components/Button/Button'
import { PhoneInput } from '~/client/src/shared/components/PhoneInput'
import { Field } from '~/client/src/shared/components/shadcn-ui/field'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import InitialState from '~/client/src/shared/stores/InitialState'
import { getPhoneCountry } from '~/client/src/shared/utils/phoneNumberValidation'

const PHONE_INPUT_ID = 'login-phone'

interface IProps {
  onSubmit: (phoneNumber: string) => void
  setSubmitBtnRef: (ref: HTMLButtonElement) => void
  state?: InitialState
}

@inject('state')
@observer
export default class PhoneNumberForm extends React.Component<IProps> {
  @observable private phoneNumber: string = ''

  private readonly validation = new FormValidation(() => this.errors)

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const { state, setSubmitBtnRef } = this.props
    const phoneNumberError = this.validation.errorOf('phoneNumber')

    return (
      <form
        noValidate
        className="tw:flex tw:flex-col tw:gap-6"
        onSubmit={this.handleFormSubmit}
      >
        <Field data-invalid={!!phoneNumberError}>
          <FieldLabelRow htmlFor={PHONE_INPUT_ID} error={phoneNumberError}>
            {Localization.translator.phoneNumber}
          </FieldLabelRow>
          <PhoneInput
            id={PHONE_INPUT_ID}
            required
            value={this.phoneNumber}
            onChange={this.handlePhoneNumberChange}
            onBlur={this.handlePhoneNumberBlur}
            aria-invalid={!!phoneNumberError || undefined}
            aria-describedby={
              phoneNumberError ? fieldErrorId(PHONE_INPUT_ID) : undefined
            }
            defaultCountry={getPhoneCountry(state.countryCode)}
            data-testid="login-phone-input"
          />
        </Field>

        <Button
          ref={setSubmitBtnRef}
          type="submit"
          className="tw:w-full"
          data-testid="login-sign-in-phone-button"
        >
          {Localization.translator.signInWithSms}
        </Button>
      </form>
    )
  }

  @computed
  private get errors(): Record<'phoneNumber', string> {
    return {
      phoneNumber: getPhoneNumberError(this.phoneNumber),
    }
  }

  @action.bound
  private handlePhoneNumberChange(phoneNumber: string) {
    this.phoneNumber = phoneNumber
  }

  private handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (
      focusFirstInvalidField(
        this.validation.attemptSubmit,
        () => PHONE_INPUT_ID,
      )
    ) {
      return
    }
    this.props.onSubmit(this.phoneNumber)
  }

  private handlePhoneNumberBlur = () => {
    this.validation.touch('phoneNumber', this.phoneNumber)
  }
}
